import React, { FormEvent, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import {
  Money as MoneyModel,
  ReservationStatus,
  TransactionType,
  UserPlan,
} from '../../../../api-client';
import appConfig from '../../../../configs/app';
import { createInvestmentManager, createReservationManager } from '../../../../di';
import { useErrorCallback, useNavigateByName, useProfileData } from '../../../../hooks';
import { cn, resultIf, RouteDictionary } from '../../../../utils';
import { CAPITAL_PROTECTION_REQUIRED_PROJECTS, CapitalProtectionBanner } from '../../../banners';
import { Button, Checkbox, InputMoney, Money, PrimaryButton, Toggle } from '../../../common';
import { PrimaryButtonColor } from '../../../common/buttons/decorators/PrimaryButton/PrimaryButton';
import { MoneyMode } from '../../../common/utils/Money/Money';
import InvestNotice from '../../../project/InvestNotice';
import { DetailedProjectContext } from '../DetailedProjectContext/DetailedProjectContext';

import './ProjectInvest.scoped.scss';

type InvestTab = 'standard' | 'capitalProtection';

const CAPITAL_PROTECTION_TERMS_URL =
  'https://cdn.conomica.ru/file/3176e/139/09d2-1ea03/usloviya-akcii-zashhitakapitala-versiya-02-06-26.pdf';

const ProjectInvest = () => {
  const { project, reservation, setReservation } = useContext(DetailedProjectContext);

  const maxInvestment = new MoneyModel(
    project.accounting.debtPrice.amount - project.accounting.investedSum.amount,
    'RUB'
  );
  const hasActiveReservation = reservation?.status === ReservationStatus.ACTIVE;

  const minInvestmentLimit = Math.min(
    maxInvestment.amount,
    project.accounting.minInvestment.amount,
    hasActiveReservation ? Number(reservation!.money.amount) : Infinity
  );

  const capitalProtectionMinInvestment = Math.min(
    maxInvestment.amount,
    appConfig.capitalProtectionMinInvestment
  );

  const [money, setMoney] = useState<MoneyModel>(new MoneyModel(minInvestmentLimit, 'RUB'));
  const [tab, setTab] = useState<InvestTab>('standard');
  const [capitalProtectionAccepted, setCapitalProtectionAccepted] = useState(false);
  const [reserveMode, setReserveMode] = useState(false);

  const handleError = useErrorCallback();
  const profileData = useProfileData();
  const [loading, setLoading] = useState(false);
  const focused = useRef<boolean>(false);

  const protectedInvestmentsCount = profileData.capitalProtectedInvestmentsCount;
  const capitalProtectionAvailable =
    protectedInvestmentsCount !== null &&
    protectedInvestmentsCount > 0 &&
    protectedInvestmentsCount < CAPITAL_PROTECTION_REQUIRED_PROJECTS;

  // Премиум-пользователь может вместо инвестиции зарезервировать сумму и проинвестировать её позже.
  // После создания резервации переключатель пропадает - блок переходит в режим инвестирования
  // зарезервированной суммы.
  const canReserve = profileData.userPlan === UserPlan.CONOMICA_PLUS && !hasActiveReservation;
  const reserveActive = canReserve && reserveMode;

  const capitalProtectionMode =
    capitalProtectionAvailable && tab === 'capitalProtection' && !reserveActive;
  const standardMode = !capitalProtectionMode;
  const activeMinInvestment = capitalProtectionMode
    ? capitalProtectionMinInvestment
    : minInvestmentLimit;

  useEffect(() => {
    if (hasActiveReservation && !focused.current) {
      setMoney(new MoneyModel(Number(reservation!.money.amount), reservation!.money.currencyCode));
    }
  }, [hasActiveReservation]);
  const { confirmationUuid } = useParams();
  const navigate = useNavigateByName();

  const investmentFee = useMemo((): MoneyModel => {
    const settings = project.feeSettings.find(
      (item) => item.transactionType === TransactionType.INVESTMENT_FEE
    );
    if (settings) {
      if (settings.percent) {
        return new MoneyModel((money.amount * settings.percent) / 100, 'RUB');
      } else if (settings.money) {
        return settings.money;
      }
    }
    return new MoneyModel(0, 'RUB');
  }, [money.amount]);

  const total: MoneyModel = useMemo((): MoneyModel => {
    return new MoneyModel(money.amount + investmentFee.amount, 'RUB');
  }, [money, investmentFee]);

  const totalReservedSum = project.accounting.reservedSum?.amount ?? 0;
  const maxAllowed = hasActiveReservation
    ? Math.min(Number(reservation!.money.amount), maxInvestment.amount)
    : Math.max(0, maxInvestment.amount - totalReservedSum);
  const reservationLimitLeft = project.reservationLimit
    ? project.reservationLimit.amount - totalReservedSum
    : Infinity;
  const reserveMax = Math.max(
    0,
    Math.min(maxInvestment.amount - totalReservedSum, reservationLimitLeft)
  );
  const activeMax = reserveActive ? reserveMax : maxAllowed;

  const disabledConditionToInvest =
    !profileData.lenderVerified ||
    profileData.passportExpired ||
    project.isReadonly ||
    money.amount < activeMinInvestment ||
    money.amount > activeMax ||
    money.amount + investmentFee.amount > profileData.balance.amount ||
    (capitalProtectionMode && !capitalProtectionAccepted) ||
    loading ||
    maxInvestment.amount === 0;

  const handleReserveToggle = () => {
    setReserveMode((value) => !value);
    setTab('standard');
    setMoney(new MoneyModel(minInvestmentLimit, 'RUB'));
  };

  const handleSubmitToReservation = async (): Promise<void> => {
    const manager = await createReservationManager();
    const result = await manager.create(project.uuid, money);
    setReserveMode(false);
    setReservation?.(result);
  };

  const handleSubmitToInvestment = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setLoading(true);
    try {
      if (reserveActive) {
        await handleSubmitToReservation();
        return;
      }
      const manager = await createInvestmentManager();
      const result = await manager.createInvestmentConfirmation(
        project.uuid,
        money,
        capitalProtectionMode
      );
      navigate(RouteDictionary.INVESTMENT_CONFIRMATION, {
        uuid: project.uuid,
        confirmationUuid: result.uuid,
      });
    } catch (error) {
      if (error instanceof Error) {
        await handleError(error);
      }
    } finally {
      setLoading(false);
    }
  };

  const renderRestrictionNotice = () => {
    if (profileData.lenderVerified && profileData.passportExpired) {
      return (
        <InvestNotice action={{ to: RouteDictionary.IDENTIFICATION, text: 'Обновить верификацию' }}>
          Срок действия вашего паспорта истёк. Обновите верификацию, добавив новый паспорт.
        </InvestNotice>
      );
    }
    if (!profileData.lenderVerified) {
      return (
        <InvestNotice action={{ to: RouteDictionary.IDENTIFICATION, text: 'Пройти верификацию' }}>
          Чтобы начать инвестировать, вам необходимо пройти верификацию.
        </InvestNotice>
      );
    }
    if (project.isReadonly) {
      return (
        <InvestNotice>
          Проект пока доступен только для ознакомления. Возможность проинвестировать скоро
          откроется.
        </InvestNotice>
      );
    }
    return null;
  };
  const restrictionNotice = renderRestrictionNotice();

  const displayAvailable = reserveActive
    ? new MoneyModel(reserveMax, 'RUB')
    : hasActiveReservation
      ? new MoneyModel(Number(reservation!.money.amount), reservation!.money.currencyCode)
      : new MoneyModel(
          Math.max(0, maxInvestment.amount - totalReservedSum),
          maxInvestment.currencyCode
        );

  return (
    <div className="detailed-project-invest">
      <CapitalProtectionBanner variant="compact" className="detailed-project-invest__promo" />
      <div
        className={cn(
          'detailed-project-invest__container',
          resultIf(confirmationUuid !== undefined, 'detailed-project-invest__container-none')
        )}
      >
        {canReserve && (
          <div className="detailed-project-invest__toggle-container">
            <Toggle
              active={reserveMode}
              onToggled={handleReserveToggle}
              disabled={loading}
              size="s"
            />
            <div
              className={cn(
                'detailed-project-invest__toggle-title',
                resultIf(reserveMode, 'active')
              )}
            >
              Зарезервировать вместо инвестирования
            </div>
          </div>
        )}

        {capitalProtectionAvailable && !reserveActive && (
          <div
            className="detailed-project-invest__tabs"
            role="tablist"
            aria-label="Режим инвестирования"
          >
            <button
              type="button"
              role="tab"
              aria-selected={standardMode}
              className={cn(
                'detailed-project-invest__tab',
                resultIf(standardMode, 'detailed-project-invest__tab--active')
              )}
              onClick={() => {
                setTab('standard');
                setMoney(new MoneyModel(minInvestmentLimit, 'RUB'));
              }}
            >
              Стандартный режим
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={capitalProtectionMode}
              className={cn(
                'detailed-project-invest__tab',
                resultIf(capitalProtectionMode, 'detailed-project-invest__tab--active')
              )}
              onClick={() => {
                setTab('capitalProtection');
                setMoney(new MoneyModel(capitalProtectionMinInvestment, 'RUB'));
              }}
            >
              Защита капитала
            </button>
          </div>
        )}

        <div className="detailed-project-invest__main">
          <div
            className={
              money.amount > activeMax
                ? 'detailed-project-invest__money detailed-project-invest__money--additional detailed-project-invest__money__error'
                : 'detailed-project-invest__money detailed-project-invest__money--additional'
            }
          >
            <div>{reserveActive ? 'Доступно для резервации:' : 'Доступно для инвестирования:'}</div>
            <div>
              <Money mode={MoneyMode.ADDITIONAL} money={displayAvailable} />
            </div>
          </div>
          {hasActiveReservation && (
            <div className="detailed-project-invest__money detailed-project-invest__money--additional">
              <div>Ваша резервация в проекте:</div>
              <div>
                <Money
                  money={
                    new MoneyModel(
                      Number(reservation!.money.amount),
                      reservation!.money.currencyCode
                    )
                  }
                />
              </div>
            </div>
          )}
          <div
            className={
              money.amount < activeMinInvestment
                ? 'detailed-project-invest__money detailed-project-invest__money--additional detailed-project-invest__money__error'
                : 'detailed-project-invest__money detailed-project-invest__money--additional'
            }
          >
            <div>Минимальная инвестиция:</div>
            <div>
              <Money
                mode={MoneyMode.ADDITIONAL}
                money={new MoneyModel(activeMinInvestment, 'RUB')}
              />
            </div>
          </div>
          <div
            className={
              money.amount + investmentFee.amount > profileData.balance.amount
                ? 'detailed-project-invest__money detailed-project-invest__money__error'
                : 'detailed-project-invest__money'
            }
          >
            <div>Средств на балансе:</div>
            <div>
              <Money money={profileData.balance} />
            </div>
          </div>
          <form className="detailed-project-invest__input" onSubmit={handleSubmitToInvestment}>
            <div className="detailed-project-invest__input-additional">
              {reserveActive
                ? 'Введите сумму резервирования'
                : capitalProtectionMode
                  ? 'Размер участия в сделке с защитой капитала'
                  : 'Введите сумму для инвестирования'}
            </div>
            <InputMoney
              money={money}
              onValueChanged={setMoney}
              onFocus={() => (focused.current = true)}
              onBlur={() => (focused.current = false)}
            />
          </form>
          <div className="detailed-project-invest__money">
            <div>Комиссия:</div>
            <div>
              <Money money={investmentFee} />
            </div>
          </div>
          <div className="detailed-project-invest__money">
            <div>Итого:</div>
            <div>
              <Money money={total} />
            </div>
          </div>
        </div>
        <form className="detailed-project-invest__submit" onSubmit={handleSubmitToInvestment}>
          {restrictionNotice}
          <PrimaryButton
            expanded
            large
            color={
              profileData.lenderVerified || !project.isReadonly
                ? PrimaryButtonColor.GREEN
                : PrimaryButtonColor.GRAY
            }
          >
            <Button disabled={disabledConditionToInvest} type="submit">
              {reserveActive ? 'Зарезервировать' : 'Инвестировать'}
            </Button>
          </PrimaryButton>

          {capitalProtectionMode && (
            <div className="detailed-project-invest__capital-protection-accept">
              <Checkbox
                checked={capitalProtectionAccepted}
                onChange={(e) => setCapitalProtectionAccepted(e.target.checked)}
              >
                Подтверждаю присоединение к условиям <br /> Акции{' '}
                <span>
                  <a
                    href={CAPITAL_PROTECTION_TERMS_URL}
                    className="detailed-project-invest__capital-protection-link"
                    target="_blank"
                    rel="noreferrer noopener"
                  >
                    “Защита капитала”
                  </a>
                </span>
              </Checkbox>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};
export default ProjectInvest;
